import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { TileCard, EmpButton as Button, EmpSpace as Space, EmpTag as Tag } from '@empowernow/ui';
import PageHeading from '../core/a11y/PageHeading';
import { fetchFormSchemaUI, type CommandKey } from '../core/services/formSchema';
import { SchemaForm } from '../components/SchemaForm';
import { useNormalize } from '../hooks/useNormalize';
import { workflowApi } from '../core/services/api';

type Problem = {
  type?: string; title?: string; detail?: string; status?: number;
  instance?: string; correlation_id?: string; fingerprint?: string;
};

export default function FormRunner() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const key: CommandKey = useMemo(() => ({
    system: params.get('system') || '',
    objectType: params.get('object_type') || '',
    action: params.get('action') || '',
  }), [loc.search]);

  const [schema, setSchema] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, any>>({});
  const [banner, setBanner] = useState<Problem | null>(null);
  const [result, setResult] = useState<any | null>(null);
  const { normalize } = useNormalize(key, 'both');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchFormSchemaUI(key).then(s => { if (mounted) setSchema(s); }).catch(e => { if (mounted) setError(e?.message || 'Failed to load form'); }).finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [key.system, key.objectType, key.action]);

  const onSubmit = async (data: Record<string, any>) => {
    setBanner(null);
    setResult(null);
    try {
      const resp: any = await workflowApi.startCommand(key.system, key.objectType, key.action, data);
      const waiting = resp?.data?.result?.waiting ?? resp?.data?.waiting ?? resp?.waiting;
      if (waiting && typeof waiting === 'object') {
        const stateVersion = waiting?.state_version ?? waiting?.headers?.['If-Match'];
        const suggestedIdem = waiting?.idempotency_key ?? waiting?.request_format?.body?.idempotency_key;
        setBanner({ title: 'WAITING', detail: `state_version=${stateVersion}, idempotency_key=${suggestedIdem}` });
      } else {
        const payload = resp?.data ?? resp;
        setResult(payload);
        setBanner({ title: 'Success', detail: 'Command completed.' });
      }
    } catch (e: any) {
      const status = e?.response?.status || 0;
      if (status === 409 || status === 412) {
        const body = e?.response?.data || {};
        setBanner({ title: body.title || (status === 409 ? 'Conflict (stale body.state_version)' : 'Precondition Failed (stale If-Match)'), detail: body.detail, status, correlation_id: body.correlation_id, fingerprint: body.fingerprint });
      } else {
        setBanner({ title: 'Error', detail: e?.message || 'Failed to start' });
      }
    }
  };

  if (loading) return <div className="glass-card p-4" role="status" aria-live="polite">Loading…</div>;
  if (error) return <div className="glass-card p-4" role="alert">{error}</div>;
  if (!schema) return null;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <PageHeading title="Form Runner">Form Runner</PageHeading>
      </div>

      {banner && (
        <TileCard theme="auto" elevation={1}>
          <div style={{ padding: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{banner.title}</div>
                {banner.detail && <div style={{ color: '#666' }}>{banner.detail}</div>}
                {banner.status && <div style={{ color: banner.status === 409 ? '#c77d00' : '#c00' }}>HTTP {banner.status}</div>}
              </div>
              <Button onClick={() => setBanner(null)}>Dismiss</Button>
            </div>
          </div>
        </TileCard>
      )}

      <TileCard theme="auto" elevation={1}>
        <div style={{ padding: 12 }}>
          <SchemaForm schema={schema} value={form} onChange={setForm} onSubmit={onSubmit} normalizeMode="both" onNormalize={(vals) => normalize(vals)} />
        </div>
      </TileCard>

      {result && (
        <TileCard theme="auto" elevation={1}>
          <div style={{ padding: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Result</div>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(result, null, 2)}</pre>
          </div>
        </TileCard>
      )}
    </Space>
  );
}


